import {
  wallOpeningHasWrapping,
  wallOpeningOffset2,
  wallOpeningOpening2,
  wallOpeningPlane,
  wallOpeningShouldMirror,
  wallOpeningSideLayer,
} from "building-common/openings/openingQuery";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { OpeningSideName } from "building-types/openings/openingTypes";
import { FRONT, BACK } from "building-types/openings/openingTypes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import type { WallSideContext } from "building-types/walls/wallSideTypes";
import type { SolidWallSide } from "building-types/walls/wallTypes";
import type { PolyLine2ById, SolidWallMass } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { LRUStringCache } from "common/utils/lruStringCache";
import { aabb2ByPolyLine2s, aabb2Overlaps } from "geom/aabb2";
import { intersectPolyLine2PolyLine2 } from "geom/intersect";
import { CurveCurveRangeIntersection } from "geom/intersectLine2Line2";
import { polyLine2OnPlane, planeProjectPolyLine3 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { dcelFromPolygons } from "geom/polyclip/dcelFromPolygons";
import { polygonUnion, polyLine2ToPolyClipPolygonLoop } from "geom/polyclip/polyClip";
import {
  polyLine2Length,
  polyLine2Copy,
  polyLine2TrimEnds,
  polyLine2AABB2,
  polyLine2NonUniformScaleAndKeepCenter,
} from "geom/polyLine2";
import { type PolyLine2, polyLine2New } from "geom/polyLine2Types";
import { openingShouldUseExteriorCasing } from "../openings/anyOpeningQuery";
import { iterableSolidWallSideOpenings } from "./iterableSolidWallSideOpenings";

/**
 * epsilon used for judging whether two loops share an edge
 * @todo(Andrew): this is a particularly large epsilon, which makes me think there is compounding error
 * somewhere that needs to be tracked down and fixed.
 */
export const UNION_LOOPS_EPSILON = 0.1;

export interface OpeningData {
  opening2ById: PolyLine2ById;
  openingById: {
    [id: string]: GeneratedDoorPlacement | GeneratedWindowPlacement | WallCut;
  };
  sideNameById: {
    [id: string]: OpeningSideName;
  };
}

export interface UnionedOpenings {
  unioned: Float64Array[];
  openingIdsByIndex: string[][];
  opening2ById: PolyLine2ById;
  openingById: { [id: string]: any };
  overlapsHostById: { [id: string]: boolean };
}

// most homes generate between 100 and 200 masses
const unionCache = new LRUStringCache<UnionedOpenings>(300);

export function unionedWallSideOpenings(
  wallFacesSourceMass: SolidWallMass, // source mass for face loop positioning
  wallSide: SolidWallSide,
  wallSideContext: WallSideContext,
): UnionedOpenings {
  const { thickness, rightFace, id: layerId } = wallFacesSourceMass;

  const cached = unionCache.get(layerId);
  if (cached) {
    return cached;
  }

  // all unioned loops will be in the plane of the right face, which means they
  // will all be projected relative to the right face's origin
  const { plane } = rightFace;

  const openingData = gatherOpeningDataFromSide(plane, wallSide, wallSideContext, thickness);

  return unionMassOpenings(wallFacesSourceMass, openingData);
}

export function unionMassOpenings(mass: SolidWallMass, openingData: OpeningData): UnionedOpenings {
  const { rightFace, id: layerId } = mass;

  const cached = unionCache.get(layerId);
  if (cached) {
    return cached;
  }

  // all unioned loops will be in the plane of the right face, which means they
  // will all be projected relative to the right face's origin
  const { plane, loops: hostLoops } = rightFace;

  const { opening2ById, openingById, sideNameById } = openingData;

  const {
    unioned,
    idsByIndex: openingIdsByIndex,
    interiorEdgesById,
    overlapsHostById,
  } = unionedLoopsById(hostLoops, opening2ById, UNION_LOOPS_EPSILON);

  for (let i = 0; i < unioned.length; i++) {
    const unionedLoop = unioned[i];

    for (const id of openingIdsByIndex[i]) {
      const { opening } = openingById[id];
      if (!opening) {
        continue;
      }

      const layer = wallOpeningSideLayer(opening, sideNameById[id], layerId);
      if (layer.union === undefined) {
        // NOTE: this modifies the opening side!
        layer.union = {
          lines: [unionedLoop],
          plane,
        };
      }
    }
  }

  for (const [id, lines] of Object.entries(interiorEdgesById)) {
    const { opening } = openingById[id];
    if (!opening) {
      continue;
    }

    const layer = wallOpeningSideLayer(opening, sideNameById[id], layerId);
    if (layer.lines === undefined) {
      // NOTE: this modifies the opening side!
      layer.lines = { lines, plane };
    }
  }

  const data = { unioned, openingIdsByIndex, opening2ById, openingById, overlapsHostById };

  unionCache.add(layerId, data);

  return data;
}

export interface UnionedLoops {
  unioned: PolyLine2[];
  idsByIndex: Array<string[]>;
  interiorEdgesById: { [id: string]: PolyLine2[] };
  overlapsHostById: { [id: string]: boolean };
}

export function unionedLoopsById(
  hostLoops: PolyLine2[],
  inputLoops: PolyLine2ById,
  epsilon = UNION_LOOPS_EPSILON,
): UnionedLoops {
  const overlapsHostById: { [id: string]: boolean } = {};
  const interiorEdgesById: { [id: string]: PolyLine2[] } = {};
  const hostEdges: PolyLine2[] = [];

  // early exit if AABB fails or there are no opening loops
  const hostAABB2 = aabb2ByPolyLine2s(hostLoops);
  if (!hostAABB2 || Object.keys(inputLoops).length === 0) {
    return {
      unioned: Object.values(inputLoops),
      idsByIndex: [Object.keys(inputLoops)],
      interiorEdgesById,
      overlapsHostById,
    };
  }

  const unioned = polygonUnion({
    pls: Object.values(inputLoops).map((l) => [l]),
    mergeCollinear: false,
  }).flat(1);

  const idsByIndex: Array<string[]> = new Array<string[]>(unioned.length);

  const hostLoopsPoly = hostLoops.map((l) => polyLine2ToPolyClipPolygonLoop(l, false, false));

  for (let i = 0; i < idsByIndex.length; i++) {
    idsByIndex[i] = [];
  }

  for (const id in inputLoops) {
    const test = inputLoops[id];

    const testAABB2 = polyLine2AABB2(test);
    if (!testAABB2) {
      continue;
    }

    // ignore loops that fail a cheap bounds check with the host face
    if (!aabb2Overlaps(hostAABB2, testAABB2, epsilon)) {
      overlapsHostById[id] = false;
      continue;
    }

    overlapsHostById[id] = true;

    const unionPolys = unioned.map((u) => [polyLine2ToPolyClipPolygonLoop(u, false, false)]);
    const interiorEdges: PolyLine2[] = [];

    // ------------------------------------- 0th polygon is the host face
    const dcel = dcelFromPolygons({ polygons: [hostLoopsPoly, ...unionPolys] });

    // iterate over edges in the DECL to find interior edges:
    // use a stride of two to handle both a halfedge and its opposite
    // at the same time
    for (let h = 0; h < dcel.halfEdges.length; h += 2) {
      const polygonIndices = [...dcel.halfEdgeToPolygon[h], ...dcel.halfEdgeToPolygon[h + 1]];

      // ignore edges that are not part of a polygon
      if (polygonIndices.length === 0) {
        continue;
      }

      // note reversed vertex order (this will produced holes rather than filled faces)
      const [x1, y1] = dcel.vertices[dcel.halfEdges[h][1]];
      const [x2, y2] = dcel.vertices[dcel.halfEdges[h][0]];

      const fullEdge = polyLine2New([x1, y1, x2, y2]);
      // ignore edges that are shorter than the slop introduced
      // by the epsilon value
      if (polyLine2Length(fullEdge) < epsilon * 8) {
        continue;
      }

      const fullAABB2 = polyLine2AABB2(fullEdge);
      if (!fullAABB2) {
        continue;
      }

      // truncate the edge by a significant margin to avoid false positives
      const truncatedEdge = polyLine2Copy(fullEdge);
      polyLine2TrimEnds(truncatedEdge, epsilon * 4);

      const truncatedAABB2 = polyLine2AABB2(truncatedEdge);
      if (!truncatedAABB2) {
        continue;
      }

      // ignore edges that are also an edge of the host face -
      // the 0th polygon is the host face
      if (polygonIndices.some((pi) => pi === 0)) {
        if (aabb2Overlaps(testAABB2, fullAABB2, epsilon) && !aabb2Overlaps(testAABB2, truncatedAABB2, epsilon)) {
          hostEdges.push(fullEdge);
        }
        continue;
      }

      // ignore edges that fail a cheap bounds check with the host face
      if (!aabb2Overlaps(hostAABB2, truncatedAABB2, epsilon)) {
        continue;
      }

      const ints = intersectPolyLine2PolyLine2(test, truncatedEdge, epsilon, undefined, false);
      if (!ints.some((int) => int.int instanceof CurveCurveRangeIntersection)) {
        continue;
      }

      interiorEdges.push(fullEdge);

      for (let i of polygonIndices) {
        if (i === 0) {
          // ignore the host face at index 0
          continue;
        }
        i--; // shift all subsequent indices down by 1
        if (!idsByIndex[i].includes(id)) {
          idsByIndex[i].push(id);
        }
      }
    }

    interiorEdgesById[id] = interiorEdges;
  }

  return { unioned, idsByIndex, interiorEdgesById, overlapsHostById };
}

/**
 * gather all loops projected into the same plane and store them by id
 */
export function gatherOpeningDataFromSide(
  hostPlane: Plane,
  wallSide: SolidWallSide,
  wallSideContext: WallSideContext,
  thickness: number,
): OpeningData {
  const { isFront } = wallSideContext;
  const sideName = isFront ? FRONT : BACK;

  // store loops by id to preserve the id for the unioned loop
  const opening2ById: PolyLine2ById = {};

  // store openings by id so that we can write the associated unioned
  // loop into the opening
  const openingById: { [id: string]: GeneratedDoorPlacement | GeneratedWindowPlacement | WallCut } = {};

  const sideNameById: { [id: string]: OpeningSideName } = {};

  let i = 0;
  for (const wallOpening of iterableSolidWallSideOpenings(wallSide)) {
    const { opening } = wallOpening;
    if (!opening) {
      continue;
    }

    let id: string;
    if (wallOpening.type !== "WALL_CUT") {
      id = wallOpening.id;
    } else {
      id = "_" + ++i;
    }

    const projected = opening2Projected(hostPlane, wallOpening, sideName, thickness);
    if (!projected) {
      continue;
    }
    opening2ById[id] = projected;
    openingById[id] = wallOpening;
    sideNameById[id] = sideName;
  }

  return { opening2ById, openingById, sideNameById };
}

export function gatherOpeningDataFromMassOpenings(
  finishMass: SolidWallMass,
  openings: { opening: GeneratedDoorPlacement | GeneratedWindowPlacement | WallCut; sideName: OpeningSideName }[],
): OpeningData {
  const {
    rightFace: { plane },
    thickness,
  } = finishMass;
  const opening2ById: PolyLine2ById = {};
  const openingById: { [id: string]: GeneratedDoorPlacement | GeneratedWindowPlacement | WallCut } = {};
  const sideNameById: { [id: string]: OpeningSideName } = {};

  let i = 0;
  for (const { opening: wallOpening, sideName } of openings) {
    const { opening } = wallOpening;
    if (!opening) {
      continue;
    }
    let id: string;
    if (wallOpening.type !== "WALL_CUT") {
      id = wallOpening.id;
    } else {
      id = "_" + ++i;
    }
    const projected = opening2Projected(plane, wallOpening, sideName, thickness);
    if (!projected) {
      continue;
    }
    opening2ById[id] = projected;
    openingById[id] = wallOpening;
    sideNameById[id] = sideName;
  }

  return { opening2ById, openingById, sideNameById };
}

function opening2Projected(
  hostPlane: Plane,
  wallOpening: GeneratedDoorPlacement | GeneratedWindowPlacement | WallCut,
  sideName: OpeningSideName,
  thickness: number,
): PolyLine2 | undefined {
  const { opening } = wallOpening;
  if (!opening) {
    return undefined;
  }

  const openingPlane = wallOpeningPlane(opening, sideName);

  // select the correct opening2 loop based on wrapping and casing settings
  const hasWrapping = wallOpeningHasWrapping(opening);
  const useCasingLoop = openingShouldUseExteriorCasing(wallOpening);

  let opening2: PolyLine2;
  if (hasWrapping && thickness > 0) {
    opening2 = wallOpeningOffset2(opening, sideName, thickness);
  } else {
    opening2 = wallOpeningOpening2(opening, sideName, useCasingLoop);
  }

  if (wallOpeningShouldMirror(opening)) {
    opening2 = polyLine2NonUniformScaleAndKeepCenter(polyLine2Copy(opening2), -1, 1);
  }

  // reproject opening2 into the host plane
  const opening3 = polyLine2OnPlane(openingPlane, opening2);
  return planeProjectPolyLine3(hostPlane, opening3);
}
